import { useSlice } from "@bridge-applications/provider-kit";
import { usePostHog } from "@posthog/react";
import { useEffect, useRef } from "react";
import { useGnomeSnapshot } from "./useGnomeSnapshot";
import { CAREER_CONTENT_SOURCE } from "../helpers/careerContentSource";
import { startupForOwner } from "../helpers/appStartup";
import {
    browserGnomeBackgroundAllowed,
    type GnomePreloadMode,
} from "../helpers/gnomePreloadPolicy";
import {
    acquireGnomeAssetFiles,
    gnomeAssetFilePlan,
    gnomeAssetFingerprint,
    gnomeAssetTransport,
} from "../helpers/gnomeSpineAssetFiles";
import { gnomePreparationQueue } from "../helpers/gnomeSpinePreparation";
import { gnomeEquipmentSelectionFromItemIds } from "../helpers/gnomeCustomization";
import {
    EMPTY_GNOME_EQUIPMENT,
    equippedItemsFromSnapshot,
} from "../helpers/gnomeSnapshotAppearance";
import { gnomeSnapshotOwnerKey } from "../helpers/gnomeSnapshotReader";
import { captureClientAnalytics } from "../helpers/analytics";
import { POSTHOG_ENABLED } from "../../state/config";
import type { GnomeAssetLease } from "../helpers/gnomeSpineAssetTransport";

const CORE_EQUIPMENT = gnomeEquipmentSelectionFromItemIds(
    EMPTY_GNOME_EQUIPMENT,
);

export function useGnomeAssetPreload(mode: GnomePreloadMode): void {
    const { state: auth } = useSlice("authSlice");
    const { state: startup, setSlice: setStartup } =
        useSlice("appStartupSlice");
    const { owner, requestSnapshot, isCurrentSession } = useGnomeSnapshot();
    const posthog = usePostHog();
    const analytics = useRef(posthog);
    analytics.current = posthog;
    const attemptedOwner = useRef<string | null>(null);
    const readyAt = owner
        ? startupForOwner(startup, owner).initialSurfaceReadyAt
        : null;
    const allowed =
        owner !== null &&
        auth.careerAccess?.userId === owner.userId &&
        auth.careerAccess.allowed;

    useEffect(() => {
        setStartup((previous) => ({
            ...startupForOwner(previous, owner),
            gnomePreloadMode: mode,
        }));
    }, [mode, owner, setStartup]);

    useEffect(() => {
        if (
            !owner ||
            !allowed ||
            readyAt === null ||
            mode === "off" ||
            CAREER_CONTENT_SOURCE !== "backend"
        )
            return;
        const key = gnomeSnapshotOwnerKey(owner);
        let cancelled = false;
        let startedAt: number | null = null;
        let finished = false;
        let phase: "core" | "outfit" | "prepare" = "core";
        let metadata: "ready" | "absent" | "failed" = "absent";
        let plan = gnomeAssetFilePlan(CORE_EQUIPMENT);
        const leases: GnomeAssetLease<void>[] = [];
        const attemptId = crypto.randomUUID();
        const actualMode =
            mode === "prepare" && typeof requestIdleCallback === "function"
                ? "prepare"
                : "bytes";
        const common = {
            attempt_id: attemptId,
            preload_mode: mode,
            effective_mode: actualMode,
            preload_strategy: "startup_idle_v1",
            asset_pack_fingerprint: gnomeAssetFingerprint,
        } as const;

        const finish = (outcome: "ready" | "error" | "cancelled") => {
            if (finished || startedAt === null) return;
            finished = true;
            leases.forEach((lease) => lease.release());
            if (POSTHOG_ENABLED && analytics.current && isCurrentSession())
                captureClientAnalytics(
                    analytics.current,
                    "gnome_spine_preload_finished",
                    {
                        ...common,
                        outcome,
                        phase,
                        metadata_outcome: metadata,
                        duration_ms: performance.now() - startedAt,
                        pack_count: plan.packIds.length,
                        ...gnomeAssetTransport.diagnostics(plan.urls),
                    },
                );
        };

        const start = async () => {
            if (
                cancelled ||
                attemptedOwner.current === key ||
                !browserGnomeBackgroundAllowed()
            )
                return;
            attemptedOwner.current = key;
            startedAt = performance.now();
            if (POSTHOG_ENABLED && analytics.current)
                captureClientAnalytics(
                    analytics.current,
                    "gnome_spine_preload_started",
                    { ...common, since_home_ready_ms: startedAt - readyAt },
                );
            try {
                const core = acquireGnomeAssetFiles(
                    CORE_EQUIPMENT,
                    "background",
                );
                leases.push(core);
                const [snapshot] = await Promise.all([
                    requestSnapshot("existing")
                        .then((result) => {
                            metadata = result ? "ready" : "absent";
                            return result;
                        })
                        .catch(() => {
                            metadata = "failed";
                            return null;
                        }),
                    core.ready,
                ]);
                if (cancelled || !isCurrentSession()) return;
                const equipment = snapshot
                    ? gnomeEquipmentSelectionFromItemIds(
                          equippedItemsFromSnapshot(snapshot),
                      )
                    : CORE_EQUIPMENT;
                phase = "outfit";
                plan = gnomeAssetFilePlan(equipment);
                const outfit = acquireGnomeAssetFiles(equipment, "background");
                leases.push(outfit);
                await outfit.ready;
                if (cancelled || !isCurrentSession()) return;
                if (actualMode === "prepare") {
                    phase = "prepare";
                    const { loadGnomeSpineAssets } =
                        await import("../helpers/gnomeSpineAssets");
                    if (cancelled) return;
                    await loadGnomeSpineAssets(equipment, "background");
                }
                if (!cancelled) finish("ready");
            } catch {
                if (!cancelled) finish("error");
            }
        };

        const update = () => {
            const permitted = !cancelled && browserGnomeBackgroundAllowed();
            gnomeAssetTransport.setBackgroundEnabled(permitted);
            gnomePreparationQueue.setEnabled(
                permitted && actualMode === "prepare",
            );
            // Cleanup of StrictMode's first effect happens before this task begins.
            if (permitted)
                queueMicrotask(() => {
                    void start();
                });
        };
        const connection =
            "connection" in navigator &&
            navigator.connection instanceof EventTarget
                ? navigator.connection
                : null;
        document.addEventListener("visibilitychange", update);
        window.addEventListener("online", update);
        window.addEventListener("offline", update);
        connection?.addEventListener("change", update);
        update();
        return () => {
            cancelled = true;
            finish("cancelled");
            leases.forEach((lease) => lease.release());
            gnomeAssetTransport.setBackgroundEnabled(false);
            gnomePreparationQueue.setEnabled(false);
            document.removeEventListener("visibilitychange", update);
            window.removeEventListener("online", update);
            window.removeEventListener("offline", update);
            connection?.removeEventListener("change", update);
        };
    }, [allowed, isCurrentSession, mode, owner, readyAt, requestSnapshot]);
}
